import { z } from 'zod'

export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Invalid email address')

export const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
    'Password must contain at least one uppercase letter, one lowercase letter, and one number'
  )

export const nameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(50, 'Name must not exceed 50 characters')
  .regex(/^[a-zA-Z\s]+$/, 'Name must contain only letters and spaces')

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
})

export const registerSchema = z.object({
  name: nameSchema,
  email: emailSchema,
  password: passwordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
})

export const courseSchema = z.object({
  title: z.string()
    .min(1, 'Title is required')
    .max(100, 'Title must not exceed 100 characters'),
  description: z.string()
    .min(1, 'Description is required')
    .max(1000, 'Description must not exceed 1000 characters'),
  category: z.string().min(1, 'Category is required'),
  tags: z.array(z.string()).optional(),
  price: z.number()
    .positive('Price must be greater than 0')
    .max(9999, 'Price cannot exceed 9999'),
  currency: z.string().default('USD'),
  duration: z.number()
    .positive('Duration must be positive')
    .max(1000, 'Duration cannot exceed 1000 hours'),
  difficulty: z.enum(['beginner', 'intermediate', 'advanced']),
})

export const lessonSchema = z.object({
  title: z.string()
    .min(1, 'Title is required')
    .max(200, 'Title must not exceed 200 characters'),
  description: z.string().max(500, 'Description must not exceed 500 characters').optional(),
  content: z.string().optional(),
  videoUrl: z.string().url('Invalid video URL').optional().or(z.literal('')),
  duration: z.number()
    .positive('Duration must be positive')
    .max(1000, 'Duration cannot exceed 1000 minutes'),
  order: z.number().int().positive('Order must be a positive integer'),
  isPreview: z.boolean().default(false),
})

export const reviewSchema = z.object({
  rating: z.number()
    .int('Rating must be an integer')
    .min(1, 'Rating must be at least 1')
    .max(5, 'Rating cannot exceed 5'),
  comment: z.string()
    .max(1000, 'Comment must not exceed 1000 characters')
    .optional(),
})

export const urlSchema = z
  .string()
  .url('Invalid URL')
  .refine((url) => {
    try {
      const parsed = new URL(url)
      return parsed.protocol === 'http:' || parsed.protocol === 'https:'
    } catch {
      return false
    }
  }, 'URL must use HTTP or HTTPS protocol')

export const phoneSchema = z
  .string()
  .regex(
    /^\+?[\d\s\-()]+$/,
    'Invalid phone number format'
  )
  .min(10, 'Phone number must be at least 10 digits')
  .max(20, 'Phone number cannot exceed 20 characters')

export const dateSchema = z
  .string()
  .refine((date) => !isNaN(Date.parse(date)), 'Invalid date format')
  .transform((date) => new Date(date))

export const timeSchema = z
  .string()
  .regex(
    /^(?:[01]\d|2[0-3]):[0-5]\d$/,
    'Invalid time format (use HH:MM)'
  )

export const couponCodeSchema = z
  .string()
  .min(3, 'Coupon code must be at least 3 characters')
  .max(20, 'Coupon code cannot exceed 20 characters')
  .regex(
    /^[a-zA-Z0-9\-]+$/,
    'Coupon code can only contain letters, numbers, and hyphens'
  )
  .transform((code) => code.toUpperCase())

export const fileSchema = z
  .object({
    size: z.number(),
    type: z.string(),
    name: z.string(),
  })
  .refine((file) => file.size <= 10 * 1024 * 1024, 'File size must not exceed 10MB')
  .refine(
    (file) => ['image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'video/mp4'].includes(file.type),
    'File type not supported'
  )

export function validateEmail(email: string): { isValid: boolean; error?: string } {
  try {
    emailSchema.parse(email)
    return { isValid: true }
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { isValid: false, error: error.errors[0].message }
    }
    return { isValid: false, error: 'Invalid email' }
  }
}

export function validatePassword(password: string): { isValid: boolean; errors: string[] } {
  try {
    passwordSchema.parse(password)
    return { isValid: true, errors: [] }
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { isValid: false, errors: error.errors.map(err => err.message) }
    }
    return { isValid: false, errors: ['Invalid password'] }
  }
}

export function getFormFieldErrors(schema: z.ZodSchema, value: unknown): string[] {
  try {
    schema.parse(value)
    return []
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.errors.map(err => err.message)
    }
    return ['Invalid value']
  }
}

export function sanitizeInput(input: string): string {
  return input
    .trim()
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/&amp;/g, '&')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&amp;')
    .replace(/>/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
}

export function isValidUUID(uuid: string): boolean {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
  return uuidRegex.test(uuid)
}

export function isValidSlug(slug: string): boolean {
  const slugRegex = /^[a-z0-9-]+$/
  return slugRegex.test(slug) && slug.length >= 3 && slug.length <= 100
}

export function isValidPrice(price: number): boolean {
  return !isNaN(price) && price >= 0 && price <= 999999.99 && /^\d+(\.\d{1,2})?$/.test(price.toString())
}

export function isValidYoutubeUrl(url: string): boolean {
  const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/
  return youtubeRegex.test(url)
}

export function extractYoutubeId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)
  return match ? match[1] : null
}

export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength).replace(/\s\S*$/, '') + '...'
}

export function formatError(error: unknown): string {
  if (error instanceof Error) {
    return error.message
  }
  if (typeof error === 'string') {
    return error
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message)
  }
  return 'An unexpected error occurred'
}

export function isValidationError(error: unknown): error is { errors: ValidationError[] } {
  return (
    error != null &&
    typeof error === 'object' &&
    'errors' in error &&
    Array.isArray(error.errors)
  )
}

export function generatePassword(length: number = 12): string {
  const lower = 'abcdefghijklmnopqrstuvwxyz'
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const numbers = '0123456789'
  const symbols = '!@#$%^&*'
  const all = lower + upper + numbers + symbols

  let password = ''
  password += lower[Math.floor(Math.random() * lower.length)]
  password += upper[Math.floor(Math.random() * upper.length)]
  password += numbers[Math.floor(Math.random() * numbers.length)]
  password += symbols[Math.floor(Math.random() * symbols.length)]

  for (let i = password.length; i < length; i++) {
    password += all[Math.floor(Math.random() * all.length)]
  }

  return password.split('').sort(() => Math.random() - 0.5).join('')
}

// New validation functions based on test requirements
export function validatePhone(phone: string): boolean {
  const phoneRegex = /^\+?[\d\s\-()]+$/
  return phoneRegex.test(phone) &&
         phone.replace(/[^\d]/g, '').length >= 10 &&
         phone.replace(/[^\d]/g, '').length <= 15
}

export function validateURL(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

export function validateRequired(value: string): boolean {
  return value !== null && value !== undefined && value.trim().length > 0
}

export function validateMinLength(value: string, min: number): boolean {
  return value.length >= min
}

export function validateMaxLength(value: string, max: number): boolean {
  return value.length <= max
}

export function validateNumber(value: string): boolean {
  return /^\d+$/.test(value)
}

export function validatePositiveNumber(value: number): boolean {
  return value > 0
}

export function validateInRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max
}

export function validateRole(role: string): boolean {
  const validRoles = ['admin', 'instructor', 'affiliate', 'student']
  return validRoles.includes(role)
}

export function validateCourseType(type: string): boolean {
  const validTypes = ['pure_online', 'hybrid']
  return validTypes.includes(type)
}

export function calculatePasswordStrength(password: string): {
  score: number
  level: 'weak' | 'medium' | 'strong'
  feedback: string[]
} {
  let score = 0
  const feedback: string[] = []

  if (password.length >= 8) score++
  else feedback.push('Password must be at least 8 characters')

  if (/[a-z]/.test(password)) score++
  else feedback.push('Use lowercase letters')

  if (/[A-Z]/.test(password)) score++
  else feedback.push('Use uppercase letters')

  if (/\d/.test(password)) score++
  else feedback.push('Use numbers')

  if (/[^\w\s]/.test(password)) score++
  else feedback.push('Use special characters')

  if (password.length >= 12) score++

  const level = score <= 2 ? 'weak' : score <= 4 ? 'medium' : 'strong'

  return {
    score: score * 20,
    level,
    feedback: feedback.length > 0 ? feedback : ['Password is strong']
  }
}

export type FormSchema = {
  login: z.infer<typeof loginSchema>
  register: z.infer<typeof registerSchema>
  course: z.infer<typeof courseSchema>
  lesson: z.infer<typeof lessonSchema>
  review: z.infer<typeof reviewSchema>
}

export type ValidationError = {
  field: string
  message: string
  value?: unknown
}